import { useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ToggleValidationButton from '../../../../components/ToggleValidationButton';
import SuccessMessageSRAMP from './components/SuccessMessageSRAMP';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Container,
  Card,
  Divider
} from '@mui/material';
import NavigateNext from '@material-ui/icons/NavigateNext';
import HeaderSession from '../../../../utils/headerSession';
import RegisterAccess from '../../../../utils/registerAccess';
import api from '../../../../services/api';

import DadosUnidadeSaudePaciente from './stepsFichaNotificacaoProdutos/stepDadosUnidadeSaude';
import InformacaoMedicamento from './stepsFichaNotificacaoProdutos/stepInformacaoMedicamento';
import DescricaoEfeitosIndesejados from './stepsFichaNotificacaoProdutos/stepDescricaoEfeitosIndesejados';
import EvolucaoTratamento from './stepsFichaNotificacaoProdutos/stepEvolucaoTratamento';
import Notificador from './stepsFichaNotificacaoProdutos/stepNotificador';


// Interface local para os props dos steps


// Tipos de ações
type ValidationAction =
  | { type: 'INIT_VALIDATION_STATE' }
  | { type: 'SHOW_VALIDATION_ERRORS'; payload: boolean }
  | { type: 'SET_STEP_ERRORS'; payload: { stepIndex: number; errors: string[] } }
  | { type: 'TOGGLE_VALIDATION_ERRORS' };

// Estado inicial
interface ValidationState {
  showValidationErrors: boolean;
  stepErrors: Record<number, string[]>;
}

const initialState: ValidationState = {
  showValidationErrors: true,
  stepErrors: {}
};

// Reducer
function validationReducer(state: ValidationState = initialState, action: ValidationAction): ValidationState {
  switch (action.type) {
    case 'INIT_VALIDATION_STATE':
      return initialState;
    case 'SHOW_VALIDATION_ERRORS':
      return { ...state, showValidationErrors: action.payload };
    case 'SET_STEP_ERRORS':
      return {
        ...state,
        stepErrors: {
          ...state.stepErrors,
          [action.payload.stepIndex]: action.payload.errors
        }
      };
    case 'TOGGLE_VALIDATION_ERRORS':
      return {
        ...state,
        showValidationErrors: !state.showValidationErrors
      };
    default:
      return state;
  }
}

interface MedicamentoData {
  nomeComercialDCI: string;
  numeroLote: string;
  doseDiaria: string;
  viaLocalAdministracao: string;
  dataCaducidade: string;
  apresentacaoDetalhes: {
    identificacaoProductoApresentacaoProductoA: boolean;
    identificacaoProductoApresentacaoProductoB: boolean;
    identificacaoProductoApresentacaoProductoC: boolean;
    identificacaoProductoApresentacaoProductoD: boolean;
    identificacaoProductoApresentacaoProductoE: boolean;
    identificacaoProductoApresentacaoProductoF: boolean;
    identificacaoProductoApresentacaoProductoG: boolean;
    identificacaoProductoApresentacaoProductoH: boolean;
    identificacaoProductoApresentacaoProductoI: boolean;
    identificacaoProductoApresentacaoProductoJ: boolean;
    identificacaoProductoApresentacaoProductoK: boolean;
    identificacaoProductoApresentacaoProductoOutros: string;
  };
  inicioTratamento: string;
  terminoTratamento: string;
  motivoTratamento: string;
  localCompra: string;
}

export default function FichaNotificacaoSRAMP() {
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const showValidationErrors = useSelector((state: any) => state.validation?.showValidationErrors || false);

  // Instância única do react-hook-form para todos os steps
  const methods = useForm({
    mode: 'onChange'
  });

  useEffect(() => {
    // Garante validação ativa por padrão ao carregar o formulário
    dispatch({ type: 'SHOW_VALIDATION_ERRORS', payload: true });
  }, [dispatch]);

  const steps = [
    'Dados da Unidade de Saúde e do Paciente',
    'Informações sobre o Medicamento',
    'Descrição dos Efeitos Indesejáveis',
    'Evolução e Tratamento',
    'Notificador'
  ];

  const getStepContent = (stepIndex: number) => {
    switch (stepIndex) {
      case 0:
        return <DadosUnidadeSaudePaciente />;
      case 1:
        return <InformacaoMedicamento />;
      case 2:
        return <DescricaoEfeitosIndesejados />;
      case 3:
        return <EvolucaoTratamento />;
      case 4:
        return <Notificador />;
      default:
        return <DadosUnidadeSaudePaciente />;
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };


  // Função de validação do step atual

  const handleNextStep = async () => {
    // Se já estiver no último step, submete em vez de avançar
    if (activeStep >= steps.length - 1) {
      await submitRecolha();
      return;
    }

    // Se validação estiver desabilitada, avança direto
    if (!showValidationErrors) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      return;
    }
    
    // Disparar evento personalizado para validar o step atual
    const validateEvent = new CustomEvent('validateCurrentStep', {
      detail: { stepIndex: activeStep }
    });
    
    let isValid = false;
    
    // Aguardar resposta da validação
    const validationPromise = new Promise<boolean>((resolve) => {
      const handleValidationResult = (event: Event) => {
        const customEvent = event as CustomEvent;
        resolve(customEvent.detail.isValid);
        window.removeEventListener('validationResult', handleValidationResult);
      };
      window.addEventListener('validationResult', handleValidationResult);
    });
    
    window.dispatchEvent(validateEvent);
    isValid = await validationPromise;
    
    if (isValid) {
      setErrorMessage('');
      // Se próximo seria o último step, deixamos avançar normalmente
      setActiveStep((prevActiveStep) => {
        const next = prevActiveStep + 1;
        return next;
      });
    } else {
      setErrorMessage('Por favor, corrija os erros nos campos destacados.');
    }
  };

  const dadosUnidadeSaude = useSelector((state: any) => state.dadosUnidadeSaude);
  const dadosNotificador = useSelector((state: any) => state.dadosNotificador);
  const dadosEfeitos = useSelector((state: any) => state.dadosEfeitosIndesejados);
  const dadosEvolucao = useSelector((state: any) => state.dadosEvolucaoTratamento);
  const informacoesMedicamento = useSelector((state: any) => state.informacoesMedicamento);
  const dadosPaciente = useSelector((state: any) => state.dadosPaciente);

  const validateStep = (_stepIndex: number): boolean => {
    return true;
  };

  const submitRecolha = async () => {
    // Valida o step Notificador antes de enviar
    const isNotifierValid = await methods.trigger([
      'nomeApelidoNotificador',
      'categoriaProfissional',
      'telefone',
      'email'
    ]);
    // console.log('[submitRecolha] Notificador valid?', isNotifierValid);
    if (!isNotifierValid) {
      setErrorMessage('Por favor, corrija os erros nos campos destacados.');
      setActiveStep(4); // Garantir que o step Notificador esteja visível
      return;
    }

    setOpen(true);
    // Mapeia opções para enums esperados pela API: "Sim" | "Não"
    // Aceita variações com/sem acento e capitalização diferente
    const mapOpcaoEnum = (v?: string) => {
      if (!v) return undefined;
      const norm = v
        .toString()
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, ''); // remove acentos
      if (norm === 'sim') return 'Sim';
      if (norm === 'nao' || norm === 'não') return 'Não';
      return undefined;
    };
    try {
      const payload = {
        unidadeSanitariaNome: String(dadosUnidadeSaude.unidadeSanitariaNome),
        unidadeSanitariaProvincia: String(dadosUnidadeSaude.unidadeSanitariaProvincia),
        unidadeSanitariaMunicipio: String(dadosUnidadeSaude.unidadeSanitariaMunicipio),
        unidadeSanitariaBairro: String(dadosUnidadeSaude.unidadeSanitariaBairro),
        unidadeSanitariaServico: String(dadosUnidadeSaude.unidadeSanitariaServico),
        unidadeSanitariaRua: String(dadosUnidadeSaude.unidadeSanitariaRua),
        unidadeSanitariaEmail: String(dadosUnidadeSaude.unidadeSanitariaEmail),
        automedicacao: !!dadosEfeitos?.tipoOcorrencia?.automedicacao,
        farmacodependencia: !!dadosEfeitos?.tipoOcorrencia?.farmacodependencia,
        erroTerapeutico: !!dadosEfeitos?.tipoOcorrencia?.erroTerapeutico,
        campanhaVacinacao: !!dadosEfeitos?.tipoOcorrencia?.campanhaVacinacao,
        conducoesArmazenamento: !!informacoesMedicamento?.conducoesArmazenamento,
        pacienteNome: String(dadosUnidadeSaude.pacienteNome),
        pacienteIdade: dadosUnidadeSaude.pacienteIdade ? Number(dadosUnidadeSaude.pacienteIdade) : '',
        pacienteSexo: String(dadosUnidadeSaude.pacienteSexo),
        pacienteProvincia: String(dadosUnidadeSaude.pacienteProvincia),
        pacienteMunicipio: String(dadosUnidadeSaude.pacienteMunicipio),
        pacienteBairro: String(dadosUnidadeSaude.pacienteBairro),
        pacienteEmail: String(dadosUnidadeSaude.pacienteEmail),
        pacienteTelefone: String(dadosUnidadeSaude.pacienteTelefone),
        pacientePeso: dadosUnidadeSaude.pacientePeso ? Number(dadosUnidadeSaude.pacientePeso) : '',
        pacienteAltura: dadosUnidadeSaude.pacienteAltura ? Number(dadosUnidadeSaude.pacienteAltura) : '',
        pacienteGestante: !!dadosUnidadeSaude.pacienteGestante,
        pacienteRaca: String(dadosUnidadeSaude.pacienteRaca),
        pacienteAntecendentesMedicosSP: !!dadosUnidadeSaude.pacienteAntecendentesMedicosSP,
        pacienteAntecendentesReaccoesAMO: !!dadosUnidadeSaude.pacienteAntecendentesReaccoesAMO,
        pacienteAntecendentesCirurgicos: !!dadosUnidadeSaude.pacienteAntecendentesCirurgicos,
        pacienteAntecendentesAbusoDependencia: !!dadosUnidadeSaude.pacienteAntecendentesAbusoDependencia,
        pacienteAntecendentesAlergiasConhecidas: !!dadosUnidadeSaude.pacienteAntecendentesAlergiasConhecidas,
        efeitosIndesejadosDescricao: String(dadosEfeitos?.breveDescricao),
        efeitosIndesejadosInicioManifestacao: String(dadosEfeitos?.prazoManifestacao?.inicioManifestacao),
        efeitosIndesejadosFimManifestacao: String(dadosEfeitos?.prazoManifestacao?.fimManifestacao),
        efeitosIndesejadosEvolucaoEfeitosRiscoVida: !!dadosEvolucao?.evolucaoEfeitos?.riscoVida,
        efeitosIndesejadosEvolucaoEfeitosMotivouHospitalizacao: !!dadosEvolucao?.evolucaoEfeitos?.motivouHospitalizacao,
        efeitosIndesejadosEvolucaoEfeitosProlongouHospitalizacao: !!dadosEvolucao?.evolucaoEfeitos?.prolongouHospitalizacao,
        efeitosIndesejadosEvolucaoEfeitosNaoRecuperouAind: !!dadosEvolucao?.evolucaoEfeitos?.naoRecuperado,
        efeitosIndesejadosEvolucaoEfeitosRecuperouSemSequelas: !!dadosEvolucao?.evolucaoEfeitos?.recuperacaoSemSequelas,
        efeitosIndesejadosEvolucaoEfeitosRecuperouComSequelas: !!dadosEvolucao?.evolucaoEfeitos?.recuperacaoComSequelas,
        efeitosIndesejadosEvolucaoEfeitosDesconhecido: !!dadosEvolucao?.evolucaoEfeitos?.desconhecido,
        efeitosIndesejadosEvolucaoEfeitosMorte: !!dadosEvolucao?.evolucaoEfeitos?.morte,
        efeitosIndesejadosEvolucaoEfeitosTratamentoCorrector: dadosEvolucao?.tratamentoCorretor ? String(dadosEvolucao?.tratamentoCorretor) : undefined,
        efeitosIndesejadosEvolucaoEfeitosSuspendeuMedicamento:
          dadosEvolucao?.suspendeuMedicamento === 'sim'
            ? true
            : dadosEvolucao?.suspendeuMedicamento === 'nao'
            ? false
            : undefined,
        efeitosIndesejadosEvolucaoEfeitosDiminuiuDoseMedicamento:
          dadosEvolucao?.diminuiuDose === 'sim'
            ? true
            : dadosEvolucao?.diminuiuDose === 'nao'
            ? false
            : undefined,
        efeitosIndesejadosEvolucaoEfeitosEfeitoIDDASDM: mapOpcaoEnum(dadosEvolucao?.efeitoDesapareceuSuspensao),
        efeitosIndesejadosEvolucaoEfeitosEfeitoIRARDM: mapOpcaoEnum(dadosEvolucao?.efeitoReapareceuReintroducao),
        efeitosIndesejadosEvolucaoEfeitosMalformacaoCongenita: !!dadosEvolucao?.evolucaoEfeitos?.malformacaoCongenita,
        notificadorNome: dadosNotificador.nomeApelidoNotificador,
        notificadorCategoriaProfissional: dadosNotificador.categoriaProfissional,
        notificadorTel: dadosNotificador.telefone,
        notificadorEmail: dadosNotificador.email
      };

      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(([_, v]) => v !== undefined && v !== '' && v !== null)
      );

      // Novo payload para identificação do produto
      let farmacoVigilanciaId: string | undefined;

      // Envia o payload principal e obtém o ID da resposta
      const responsePrincipal = await api.post('/farmaco-vigilancia/create/NRAMPS', filteredPayload);
      console.log(responsePrincipal);
      farmacoVigilanciaId = responsePrincipal?.data?.farmacoVigilancia?._id || responsePrincipal?.data?.id || responsePrincipal?.data?._id;

      if (!farmacoVigilanciaId) {
        alert('Erro: farmacoVigilanciaId não foi retornado pela API. Verifique o backend ou o payload enviado.');
        setOpen(false);
        return;
      }

      // Filtra medicamentos preenchidos
      const medicamentosPreenchidos = informacoesMedicamento?.medicamentos?.filter((m: MedicamentoData) => m.nomeComercialDCI && m.nomeComercialDCI.trim() !== '');
      if (!medicamentosPreenchidos || medicamentosPreenchidos.length === 0) {
        alert('Preencha pelo menos um medicamento com Nome Comercial/DCI para enviar.');
        setOpen(false);
        return;
      }
      const med = medicamentosPreenchidos[0];
      const produtoPayload = {
        farmacoVigilanciaId,
        identificacaoProductoNomeDCI: med.nomeComercialDCI,
        identificacaoProductoNumeroLote: med.numeroLote || undefined,
        identificacaoProductoDoseDiaria: med.doseDiaria || undefined,
        identificacaoProductoViaLocalAdmin: med.viaLocalAdministracao || undefined,
        identificacaoProductoInicioDateToma: med.inicioTratamento || undefined,
        identificacaoProductoTerminoDateToma: med.terminoTratamento || undefined,
        identificacaoProductoMotivoTratamento: med.motivoTratamento || undefined,
        identificacaoProductoLocalCompra: med.localCompra || undefined,
        identificacaoProductoDataExpiracao: med.dataCaducidade || undefined,
        identificacaoProductoApresentacaoProductoA: typeof med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoA === 'boolean' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoA : undefined,
        identificacaoProductoApresentacaoProductoB: typeof med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoB === 'boolean' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoB : undefined,
        identificacaoProductoApresentacaoProductoC: typeof med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoC === 'boolean' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoC : undefined,
        identificacaoProductoApresentacaoProductoD: typeof med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoD === 'boolean' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoD : undefined,
        identificacaoProductoApresentacaoProductoE: typeof med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoE === 'boolean' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoE : undefined,
        identificacaoProductoApresentacaoProductoF: typeof med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoF === 'boolean' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoF : undefined,
        identificacaoProductoApresentacaoProductoG: typeof med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoG === 'boolean' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoG : undefined,
        identificacaoProductoApresentacaoProductoH: typeof med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoH === 'boolean' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoH : undefined,
        identificacaoProductoApresentacaoProductoI: typeof med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoI === 'boolean' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoI : undefined,
        identificacaoProductoApresentacaoProductoJ: typeof med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoJ === 'boolean' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoJ : undefined,
        identificacaoProductoApresentacaoProductoK: typeof med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoK === 'boolean' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoK : undefined,
        identificacaoProductoApresentacaoProductoOutros: med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoOutros || undefined
      };

      // Envia o produto após obter o ID
      const responseProduto = await api.patch('/farmaco-vigilancia/NRAMPS/add-producto', produtoPayload);

      // Sucesso: limpar estados e mostrar mensagem
      setShowSuccess(true);
      setOpen(false);
      // Reset Redux slices específicos da farmacovigilância
      dispatch({ type: 'RESET_FARMACOVIGILANCIA' });
      // Reset do formulário RHF e voltar para o primeiro passo
      methods.reset();
      setActiveStep(0);
    } catch (error: any) {
      console.log(error);
    }
  };

  return (
    <Container style={{ marginBottom: 12, minHeight: "70vh" }}>
      {showSuccess ? (
        <SuccessMessageSRAMP message="Notificação enviada com sucesso!" onClose={() => setShowSuccess(false)} />
      ) : (
        <>
          {/* <ToggleValidationButton /> */}
          <RegisterAccess page={'ficha de notificacoes de suspeita de reacções adversas a medicamentos'} />
          <HeaderSession title='NOTIFICAÇÃO DE SUSPEITA DE REACÇÕES ADVERSAS A MEDICAMENTOS E PRODUTOS SANITÁRIOS' />
          <Card style={{ marginBottom: 12, padding: 22 }}>
            <FormProvider {...methods}>
              <form>
                <div style={{ overflow: 'auto' }}>
                  <Stepper color="secondary" activeStep={activeStep} alternativeLabel>
                    {steps.map((label) => (
                      <Step key={label} >
                        <StepLabel style={{ color: '#85287e' }}>
                          {label}
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                  <Divider />
                </div>
              {activeStep === 0 && (
                <DadosUnidadeSaudePaciente />
              )}
              {activeStep === 1 && (
                <InformacaoMedicamento />
              )}
              {activeStep === 2 && (
                <DescricaoEfeitosIndesejados />
              )}
              {activeStep === 3 && (
                <EvolucaoTratamento />
              )}
              {activeStep === 4 && (
                <Notificador />
              )}
              
              {/* Exibir mensagens de erro de validação */}
              {errorMessage && (
                <div style={{ 
                  backgroundColor: '#ffebee', 
                  color: '#c62828', 
                  padding: '16px', 
                  margin: '16px 0', 
                  borderRadius: '4px',
                  border: '1px solid #ef5350'
                }}>
                  <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                    {errorMessage}
                  </Typography>
                </div>
              )}
              
              <div style={{ display: 'flex', margin: '20px 10px' }}>
                <Button
                  style={{ background: '#ebebf4' }}
                  variant="outlined"
                  disabled={activeStep === 0}
                  onClick={handleBack}
                >
                  Voltar
                </Button>
                <Box style={{ display: 'flex', flexGrow: 1 }} />
                {activeStep === steps.length - 1 ? (
                  <Button
                    style={{ background: '#85287e',  color:'white', width: 120 }}
                    variant="contained"
                    onClick={submitRecolha}
                  >
                    Enviar <NavigateNext />
                  </Button>
                ) : (
                  <Button
                    style={{ background: '#85287e', color:'white', width: 120 }}
                    variant="contained"
                    onClick={handleNextStep}
                  >
                    Próximo <NavigateNext />
                  </Button>
                )}
              </div>
            </form>
          </FormProvider>
        </Card>
      </>
    )}
  </Container>
);
}